import { NODE_MIN_HEIGHT } from '../constants/csmlLayout';

/**
 * Align / distribute helpers for a multi-selection. Pure: they return the
 * `{ [nodeId]: { x, y } }` positions to apply and leave the nodes untouched.
 *
 * Every card is the same width, so aligning left edges and aligning centre
 * lines would be the same move - the horizontal axis only needs "left". On the
 * vertical axis heights differ, so both "top" and "middle" are offered.
 */
export const ALIGN_ACTIONS = [
  'left',
  'top',
  'middle',
  'distributeX',
  'distributeY',
];

export function alignNodes(nodes, ids, action, heights = {}) {
  const picked = nodes.filter(node => ids.includes(node.id));
  if (picked.length < 2) return {};
  const heightOf = node => heights[node.id] || NODE_MIN_HEIGHT;
  const positions = {};

  if (action === 'left') {
    const x = Math.min(...picked.map(node => node.x));
    picked.forEach(node => {
      positions[node.id] = { x, y: node.y };
    });
  } else if (action === 'top') {
    const y = Math.min(...picked.map(node => node.y));
    picked.forEach(node => {
      positions[node.id] = { x: node.x, y };
    });
  } else if (action === 'middle') {
    const centres = picked.map(node => node.y + heightOf(node) / 2);
    const centre = (Math.min(...centres) + Math.max(...centres)) / 2;
    picked.forEach(node => {
      positions[node.id] = { x: node.x, y: centre - heightOf(node) / 2 };
    });
  } else if (action === 'distributeX' && picked.length > 2) {
    const sorted = [...picked].sort((a, b) => a.x - b.x);
    const first = sorted[0].x;
    const step = (sorted[sorted.length - 1].x - first) / (sorted.length - 1);
    sorted.forEach((node, index) => {
      positions[node.id] = { x: first + step * index, y: node.y };
    });
  } else if (action === 'distributeY' && picked.length > 2) {
    const sorted = [...picked].sort((a, b) => a.y - b.y);
    const last = sorted[sorted.length - 1];
    const span = last.y + heightOf(last) - sorted[0].y;
    const used = sorted.reduce((sum, node) => sum + heightOf(node), 0);
    const gap = (span - used) / (sorted.length - 1);
    let cursor = sorted[0].y;
    sorted.forEach(node => {
      positions[node.id] = { x: node.x, y: cursor };
      cursor += heightOf(node) + gap;
    });
  }
  return positions;
}
